import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Field,
  RadioGroup,
  Radio,
  Spinner,
} from '@fluentui/react-components';
import { DismissRegular, AttachRegular } from '@fluentui/react-icons';

interface CreateTenderingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, listFile: File | null, tenderingName: string, excelType: number) => void;
  loading: boolean;
}

const CreateTenderingDialog: React.FC<CreateTenderingDialogProps> = ({
  isOpen,
  onClose,
  onUpload,
  loading,
}) => {
  const [tenderingName, setTenderingName] = useState('');
  const [excelType, setExcelType] = useState(0);
  const [excel, setExcel] = useState<File | null>(null);
  const [excelList, setExcelList] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const listFileInputRef = useRef<HTMLInputElement>(null);

  const resetAndClose = () => {
    setTenderingName('');
    setExcelType(0);
    setExcel(null);
    setExcelList(null);
    onClose();
  };

  const handleUpload = () => {
    if (!excel || !tenderingName.trim() || !excelType) return;
    onUpload(excel, excelList, tenderingName.trim(), excelType);
  };

  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && resetAndClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={resetAndClose}
              />
            }
          >
            Skapa ny ramavtal
          </DialogTitle>
          <DialogContent className="min-w-[400px]">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Spinner size="large" />
              </div>
            ) : (
              <div className="space-y-4">
                <Field label="Avtals namn">
                  <Input
                    className="w-full"
                    value={tenderingName}
                    onChange={(e) => setTenderingName(e.target.value)}
                  />
                </Field>

                <Field label="Mall">
                  <RadioGroup
                    value={excelType ? excelType.toString() : ''}
                    onChange={(_, data) => setExcelType(Number(data.value))}
                    layout="horizontal"
                  >
                    <Radio value="1" label="Template 1" />
                    <Radio value="2" label="Template 2" />
                  </RadioGroup>
                </Field>

                <Field label="Excel fil">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setExcel(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  <Button
                    appearance="outline"
                    icon={<AttachRegular />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {excel ? excel.name : 'Välja excel fil'}
                  </Button>
                </Field>

                {excelType === 1 && (
                  <Field label="Lista excel fil">
                    <input
                      ref={listFileInputRef}
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => setExcelList(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                    <Button
                      appearance="outline"
                      icon={<AttachRegular />}
                      onClick={() => listFileInputRef.current?.click()}
                    >
                      {excelList ? excelList.name : 'Välja lista excel fil'}
                    </Button>
                  </Field>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              onClick={handleUpload}
              disabled={loading || !excel || !tenderingName.trim() || !excelType}
            >
              Ladda fil
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateTenderingDialog;
